import { useMemo, useRef } from 'react';
import { ArrowUpAZ, ArrowDownZA } from 'lucide-react';
import { ColumnDef, ColumnId, DisplayOptions, FormatsState, SortState, TotalsState } from './types';
import { cellRule, COLOR_PRESETS, rowFormat } from './formats';
import { inlineTotal } from './totals';
import { ConditionalIcon } from './ConditionalIcon';
import { GroupMode } from './savedViews';
import { cn } from '@/lib/utils';

const MIN_COLUMN_PX = 16;

interface CompactOperationsTableProps<T extends { id: string }> {
  rows: T[];
  columns: ColumnDef<T>[];
  formats: FormatsState;
  display: DisplayOptions;
  sort: SortState;
  totals?: TotalsState;
  columnWidths?: Record<string, number>;
  /** When provided, headers grow a drag handle that writes back via this callback. */
  onResizeColumn?: (columnId: string, width: number | undefined) => void;
  groupBy?: ColumnId;
  groupMode?: GroupMode;
}

export function CompactOperationsTable<T extends { id: string }>({
  rows,
  columns,
  formats,
  display,
  sort,
  totals,
  columnWidths,
  onResizeColumn,
  groupBy,
  groupMode = 'detail',
}: CompactOperationsTableProps<T>) {
  const visibleColumns = columns.filter((c) => !display.hiddenColumns.has(c.id));
  const rowPadding =
    display.rowSpacing === 'loose'
      ? 'px-2 py-2.5'
      : display.rowSpacing === 'compact'
        ? 'px-1.5 py-0'
        : 'px-2 py-1';

  const groupColumn = groupBy ? columns.find((c) => c.id === groupBy) ?? null : null;
  const groups = useMemo(() => {
    if (!groupColumn) return null;
    const map = new Map<string, T[]>();
    for (const r of rows) {
      const k = groupColumn.accessor(r) ?? '';
      const arr = map.get(k);
      if (arr) arr.push(r);
      else map.set(k, [r]);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [groupColumn, rows]);

  if (rows.length === 0) {
    return <div className="text-xs text-slate-400 px-2 py-4 text-center">No records</div>;
  }

  const hasExplicitWidths = !!columnWidths && Object.keys(columnWidths).length > 0;
  // If every visible column has an explicit width, don't let the table stretch
  // to fill its container — keep columns at their declared widths and let the
  // surrounding wrapper scroll horizontally if needed.
  const allWidthsSet =
    hasExplicitWidths &&
    visibleColumns.every((c) => typeof columnWidths?.[c.id] === 'number');
  const isSummary = !!groupColumn && groupMode === 'summary';

  const renderHeader = () => (
    <thead className="bg-slate-50 border-b border-slate-200">
      <tr>
        {visibleColumns.map((col) => {
          const sortIdx = sort.findIndex((s) => s.columnId === col.id);
          const isSorted = sortIdx >= 0;
          const dir = isSorted ? sort[sortIdx].direction : null;
          const w = columnWidths?.[col.id];
          return (
            <th
              key={col.id}
              style={w ? { width: w } : undefined}
              className="relative px-2 py-1.5 text-left uppercase tracking-wider text-[10px] font-semibold text-slate-600 whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1">
                {col.label}
                {isSorted && (
                  <span className="inline-flex items-center gap-0.5 text-slate-900">
                    {dir === 'asc' ? (
                      <ArrowUpAZ className="w-3 h-3" />
                    ) : (
                      <ArrowDownZA className="w-3 h-3" />
                    )}
                    {sort.length > 1 && (
                      <span className="text-[9px] leading-none font-bold">{sortIdx + 1}</span>
                    )}
                  </span>
                )}
              </span>
              {onResizeColumn && (
                <ResizeHandle
                  currentWidth={w}
                  onResize={(next) => onResizeColumn(col.id, next)}
                  onReset={() => onResizeColumn(col.id, undefined)}
                />
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const renderGroupHeader = (key: string, groupRows: T[]) => {
    const anyTotal = totals && visibleColumns.some((c) => totals.has(c.id));
    const label = (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-900 font-semibold">{key || <span className="italic text-slate-400">(empty)</span>}</span>
        <span className="text-slate-400 font-normal">·</span>
        <span className="text-slate-500 font-normal">{groupRows.length}</span>
      </span>
    );
    if (anyTotal && totals) {
      return (
        <tr className="border-y border-slate-200 bg-slate-100/80">
          {visibleColumns.map((col, idx) => (
            <td
              key={col.id}
              className={cn(
                'px-2 py-1 text-[11px] font-medium whitespace-nowrap',
                idx > 0 && 'text-center text-slate-600'
              )}
            >
              {idx === 0 ? label : totals.has(col.id) ? inlineTotal(col, groupRows) : ''}
            </td>
          ))}
        </tr>
      );
    }
    return (
      <tr className="border-y border-slate-200 bg-slate-100/80">
        <td
          colSpan={visibleColumns.length}
          className="px-2 py-1 text-[11px] font-semibold text-slate-700 whitespace-nowrap"
        >
          {label}
        </td>
      </tr>
    );
  };

  const renderRow = (row: T, idx: number) => {
    const rowColor = rowFormat(row, columns, formats);
    const rowPalette = rowColor ? COLOR_PRESETS[rowColor] : null;
    const zebra =
      display.zebra && !rowPalette && idx % 2 === 1 ? 'bg-slate-50/60' : '';
    return (
      <tr
        key={row.id}
        className={cn(
          'border-b border-slate-100 last:border-b-0',
          rowPalette ? cn(rowPalette.bg, rowPalette.text) : zebra
        )}
      >
        {visibleColumns.map((col) => {
          const rule = cellRule(row, col, formats);
          const accessorValue = col.accessor(row);
          const baseContent = accessorValue || <span className="text-slate-300">—</span>;
          const content = rule?.icon ? (
            <span className="inline-flex items-center gap-1.5">
              <ConditionalIcon name={rule.icon} color={rule.iconColor ?? rule.color} />
              {!rule.hideValue && <span>{baseContent}</span>}
            </span>
          ) : (
            baseContent
          );
          const bgClass = rule?.bgColor
            ? COLOR_PRESETS[rule.bgColor].bg
            : rule?.color
              ? COLOR_PRESETS[rule.color].bg
              : '';
          const textClass = rule?.textColor
            ? COLOR_PRESETS[rule.textColor].text
            : rule?.color
              ? COLOR_PRESETS[rule.color].text
              : '';
          const w = columnWidths?.[col.id];
          return (
            <td
              key={col.id}
              style={w ? { width: w, maxWidth: w } : undefined}
              className={cn(
                rowPadding,
                w ? 'truncate overflow-hidden' : 'whitespace-nowrap',
                bgClass,
                textClass,
                (bgClass || textClass) && 'font-medium',
                !bgClass && !textClass && 'text-slate-900'
              )}
            >
              {content}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table
          className={cn('text-xs', allWidthsSet ? 'w-auto' : 'w-full')}
          style={hasExplicitWidths ? { tableLayout: 'fixed' } : undefined}
        >
          {renderHeader()}
          <tbody>
            {groups ? (
              groups.map(([groupKey, groupRows]) => (
                <FragmentRow
                  key={groupKey}
                  header={renderGroupHeader(groupKey, groupRows)}
                  rows={isSummary ? null : groupRows.map((r, i) => renderRow(r, i))}
                />
              ))
            ) : (
              rows.map((r, i) => renderRow(r, i))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentRow({
  header,
  rows,
}: {
  header: React.ReactNode;
  rows: React.ReactNode[] | null;
}) {
  return (
    <>
      {header}
      {rows}
    </>
  );
}

interface ResizeHandleProps {
  currentWidth: number | undefined;
  onResize: (width: number) => void;
  onReset: () => void;
}

function ResizeHandle({ currentWidth, onResize, onReset }: ResizeHandleProps) {
  const draggingRef = useRef(false);
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement | null;
    const startWidth = currentWidth ?? (th ? th.getBoundingClientRect().width : 120);
    const startX = e.clientX;
    draggingRef.current = true;
    const onMove = (moveEv: MouseEvent) => {
      if (!draggingRef.current) return;
      const next = Math.max(MIN_COLUMN_PX, Math.round(startWidth + (moveEv.clientX - startX)));
      onResize(next);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  return (
    <span
      onMouseDown={handleMouseDown}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
      className="absolute top-0 right-0 z-10 h-full w-1.5 -mr-px cursor-col-resize bg-transparent hover:bg-blue-300/60 active:bg-blue-400/80"
      title="Drag to resize · Double-click to reset"
    />
  );
}
